// ── ACCOUNT INVITATIONS ──────────────────────────────────────────────
// No VIEWS entries of its own — pure helpers consumed by admin-accounts.js
// and admin-users.js. inviteState/inviteStatePill/sendInvitation/bulkInvite/
// resendInvitation/stripeModal/toggleActive are real exports, wired via each
// consumer's own VIEW_INIT (phase 2 of the onclick → addEventListener
// migration) instead of window/onclick. confirmBulkInvite/saveStripeId/
// unlinkStripe stay file-local — they're only ever reached from this file's
// own openModal() output, attached directly inline the same way
// billing-shared.js's cardModal attaches its Save button.
//
// Three states, derived from data:
//   'active'      — has logged in and set their own password (lastLogin set, not mustChangePassword)
//   'invited'     — invitation sent, hasn't activated yet (invitedAt set, never logged in)
//   'not_invited' — account exists but no invite sent yet (imported accounts land here)
import { apiFetch } from '../core/api.js';
import { DB, currentView } from '../core/state.js';
import { TODAY } from '../core/format.js';
import { isParent } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { refresh } from '../core/routing.js';

export function inviteState(u) {
  if (u.lastLogin && !u.mustChangePassword) return 'active';
  if (u.invitedAt) return 'invited';
  return 'not_invited';
}
function inviteStateLabel(s) {
  return s === 'active' ? 'Active' : s === 'invited' ? 'Invited — pending' : 'Not invited';
}
export function inviteStatePill(s) {
  const cls = s === 'active' ? 'pill-active' : s === 'invited' ? 'pill-amber' : 'pill-off';
  return `<span class="pill ${cls}">${inviteStateLabel(s)}</span>`;
}

export async function sendInvitation(userId, opts = {}) {
  const u = DB.users.find(x => x.id === userId);
  if (!u) return;
  try {
    const result = await apiFetch('/accounts/' + userId + '/invite', { method: 'POST' });
    u.invitedAt = TODAY.toISOString().slice(0,10);
    u.mustChangePassword = true;
    if (!opts.silent) {
      refresh();
      toast(result.message || `Invitation sent to ${u.firstName} ${u.lastName}`);
    }
  } catch (e) { if (!opts.silent) toastError(e.message); }
}

export function bulkInvite() {
  const targets = DB.users.filter(u => isParent(u) && u.active && inviteState(u) === 'not_invited');
  if (!targets.length) { toast('No accounts are waiting to be invited'); return; }
  openModal(`
    <h3>Send invitations</h3>
    <p class="card-intro">Send a welcome email with a temporary password to <b style="color:var(--cream)">${targets.length} ${targets.length === 1 ? 'family' : 'families'}</b> who haven't been invited yet? Each parent will be able to log in and set their own password. Already-invited and active accounts are skipped.</p>
    <div style="display:flex;gap:10px">
      <button class="btn" id="bulkInviteConfirmBtn" style="flex:1">Send ${targets.length} invitation${targets.length === 1 ? '' : 's'}</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('bulkInviteConfirmBtn').addEventListener('click', confirmBulkInvite);
}

async function confirmBulkInvite() {
  try {
    const result = await apiFetch('/accounts/bulk-invite', { method: 'POST' });
    // Refresh local data to reflect invited status
    const accounts = await apiFetch('/accounts');
    DB.users = accounts;
    closeModal();
    if (currentView === 'a-accounts') renderAccountTable(); else refresh();
    toast(result.message || `${result.count} invitation${result.count === 1 ? '' : 's'} sent`);
  } catch (e) { toastError(e.message); }
}

export function resendInvitation(userId) {
  const u = DB.users.find(x => x.id === userId);
  if (!u) return;
  const activated = inviteState(u) === 'active';
  openModal(`
    <h3>Resend invitation</h3>
    <p class="card-intro">Send ${esc(u.firstName)} ${esc(u.lastName)} a fresh invitation with a new temporary password?${activated ? ' This account has already been activated — resending will require them to set a new password on their next login.' : ''}</p>
    <div style="display:flex;gap:10px">
      <button class="btn" id="resendInviteBtn" style="flex:1">Send invitation</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('resendInviteBtn').addEventListener('click', () => { closeModal(); sendInvitation(u.id); });
}

export function stripeModal(userId) {
  const u = DB.users.find(x => x.id === userId);
  const linked = u.stripeId && u.stripeId.startsWith('cus_');
  openModal(`
    <h3>Stripe customer — ${esc(u.firstName)} ${esc(u.lastName)}</h3>
    <p class="card-intro">
      ${linked
        ? 'This account is linked to a Stripe customer. You can update the ID if the customer was recreated in Stripe, or unlink it to remove the association.'
        : 'Paste the Stripe Customer ID (starts with <b style="color:var(--cream)">cus_</b>) from your Stripe dashboard to connect this family\'s payment history and saved card.'}
    </p>
    ${linked ? `
      <div style="padding:10px 14px;background:rgba(52,211,153,0.06);border:1px solid rgba(52,211,153,0.25);border-radius:7px;margin-bottom:14px">
        <div class="detail-label">Current Stripe Customer ID</div>
        <div style="font-size:13px;color:var(--cream);font-family:monospace">${esc(u.stripeId)}</div>
      </div>` : ''}
    <div class="field"><label>Stripe Customer ID</label><input type="text" id="smStripeId" value="${esc(u.stripeId)}" placeholder="cus_XXXXXXXXXXXX" spellcheck="false"></div>
    <span class="error-msg" id="smErr">Must be a valid Stripe Customer ID starting with cus_</span>
    <div style="display:flex;gap:10px;margin-top:6px">
      <button class="btn" id="smSaveBtn" style="flex:1">Save</button>
      ${linked ? `<button class="btn-ghost btn-danger" id="smUnlinkBtn">Unlink</button>` : ''}
    </div>
    <p style="font-size:11px;color:var(--muted);margin-top:12px;line-height:1.6">Find this in the Stripe dashboard under Customers. In production, new accounts auto-create a Stripe customer on sign-up — this field is for manually connecting accounts that existed before the portal was set up.</p>`);
  document.getElementById('smSaveBtn').addEventListener('click', () => saveStripeId(u.id));
  const unlinkBtn = document.getElementById('smUnlinkBtn');
  if (unlinkBtn) unlinkBtn.addEventListener('click', () => unlinkStripe(u.id));
}
async function saveStripeId(userId) {
  const val = document.getElementById('smStripeId').value.trim();
  const err = document.getElementById('smErr');
  if (!val.startsWith('cus_') || val.length < 8) { err.classList.add('show'); return; }
  err.classList.remove('show');
  try {
    await apiFetch('/accounts/' + userId + '/stripe', { method: 'POST', body: JSON.stringify({ stripeId: val }) });
    const u = DB.users.find(x => x.id === userId);
    if (u) u.stripeId = val;
    closeModal();
    if (currentView === 'a-accounts') renderAccountTable(); else refresh();
    toast('Stripe customer linked');
  } catch (e) { toastError(e.message); }
}
async function unlinkStripe(userId) {
  if (!confirm('Remove the Stripe link for this account? Their payment history in Stripe will not be deleted.')) return;
  try {
    await apiFetch('/accounts/' + userId + '/stripe', { method: 'DELETE' });
    const u = DB.users.find(x => x.id === userId);
    if (u) u.stripeId = '';
    closeModal();
    if (currentView === 'a-accounts') renderAccountTable(); else refresh();
    toast('Stripe customer unlinked');
  } catch (e) { toastError(e.message); }
}
export async function toggleActive(userId) {
  try {
    const result = await apiFetch('/accounts/' + userId + '/toggle-active', { method: 'POST' });
    const u = DB.users.find(x => x.id === userId);
    if (u) u.active = result.active;
    if (currentView === 'a-accounts') renderAccountTable(); else refresh();
    toast(result.active ? 'Account reactivated' : 'Account deactivated');
  } catch (e) { toastError(e.message); }
}
